import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getListOrganizationCategoriesQueryKey,
  getListOrganizationCategoryFoldersQueryKey,
  useDeleteOrganizationCategoryFolder,
} from "~/api/organization/organization";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";

interface DeleteFolderAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  folderId: number;
  hasCategories: boolean;
}

export const DeleteFolderAlertDialog: FC<DeleteFolderAlertDialogProps> = ({
  open,
  setOpen,
  folderId,
  hasCategories,
}) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: deleteFolder, isPending } =
    useDeleteOrganizationCategoryFolder();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();

  const handleDeleteFolder = async () => {
    try {
      await deleteFolder({
        organizationId,
        folderId,
      });

      queryClient.invalidateQueries({
        queryKey: getListOrganizationCategoryFoldersQueryKey(organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: getListOrganizationCategoriesQueryKey(organizationId),
      });

      setOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "folder.deleteError" }),
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage id="folder.delete.title" />
          </AlertDialogTitle>
          <AlertDialogDescription>
            {hasCategories ? (
              <FormattedMessage id="folder.delete.descriptionWithCategories" />
            ) : (
              <FormattedMessage id="folder.delete.description" />
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDeleteFolder}
            disabled={isPending}
          >
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
